/**
 * Scheduling logic for service-worker update discovery (Brief UPDATE-PROMPT).
 *
 * The browser only re-fetches sw.js on its own schedule (page load / rare
 * background rechecks), so a long-open tab never learns about a deploy. This
 * checker asks the registration to re-check on a fixed interval while the tab
 * is open, plus immediately when the tab regains focus — throttled so rapid
 * tab-switching doesn't hammer the server (each check is one small GET).
 *
 * Kept free of React and of the service-worker API itself (the check is an
 * injected callback) so the timing rules are unit-testable with fake timers.
 */

/** Periodic re-check while a tab sits open (covers an always-focused tab). */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** Minimum gap between checks when focus events arrive in quick succession. */
export const FOCUS_CHECK_MIN_GAP_MS = 60 * 1000;

/** How long Dismiss hides the prompt before it may reappear. Never permanent. */
export const DISMISS_SNOOZE_MS = 30 * 60 * 1000;

export type UpdateChecker = {
  /** Begin the periodic check. Idempotent. */
  start: () => void;
  /** Stop the periodic check. Idempotent. */
  stop: () => void;
  /** Tab regained focus/visibility — check now unless one ran very recently. */
  handleFocus: () => void;
};

export function createUpdateChecker(
  check: () => unknown,
  intervalMs: number = UPDATE_CHECK_INTERVAL_MS,
  minFocusGapMs: number = FOCUS_CHECK_MIN_GAP_MS,
): UpdateChecker {
  let timer: ReturnType<typeof setInterval> | null = null;
  // Registration itself just checked, so the clock starts now — a focus event
  // in the first minute after load must not trigger a redundant check.
  let lastCheckAt = Date.now();

  const run = () => {
    lastCheckAt = Date.now();
    check();
  };

  return {
    start() {
      if (timer !== null) return;
      timer = setInterval(run, intervalMs);
    },
    stop() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    },
    handleFocus() {
      if (Date.now() - lastCheckAt >= minFocusGapMs) run();
    },
  };
}
